import includes from 'lodash/includes';
import values from 'lodash/values';
import { upperFirst } from 'lodash';

import type { PartnerName } from '@jerry-serverless/analyticslib/partner';
import { getFirstName } from '@jerry-serverless/name-parser';

import type { JerryLogoColor } from '../../components/logo/Logo';
import type { AppData } from '../../types/app_data';

import CopilotLogo from './assets/copilot-logo.png';
import CBAutoGroupAdvantageLogo from './assets/cbautogroup-advantage-logo.png';
import CBAutoGroupUnionLogo from './assets/cbautogroup-union-logo.png';
import ExperianLogo from './assets/experian-logo.png';
import LyftLogo from './assets/lyft-logo.svg';
import NondaLogo from './assets/nonda-logo.png';
import MintLogo from './assets/mint-logo.png';
import StashLogo from './assets/stash-logo.png';
import FionaLogo from './assets/fiona-logo.svg';
import WonoloLogo from './assets/wonolo-logo.png';
import BumperLogo from './assets/bumper-logo.svg';
import BennverifiedLogo from './assets/beenverified-logo.svg';
import { getAppContainerProps as origGetAppContainerProps } from './partner_container_props';
import type { ShopForOptionsType as OrigShopForOptionsType } from './partner_shopfor_options';
import { getShopForOptions as origGetShopForOptions } from './partner_shopfor_options';
import {
  INTERSTITIAL_ENABLED_LIST as OrigInterstitialList,
  WELCOME_ENABLED_LIST as OrigWelcomeEnabledList,
  CUSTOM_LANDING_ENABLED_LIST as OrigCustomLandingEnabledList,
  CUSTOM_PREFILLED_PAGE_PARTNER_LIST as OrigCustomPrefilledPagePartnerList,
} from './partner_interstitial_list';

// Partner interstitial list
export const INTERSTITIAL_ENABLED_LIST = OrigInterstitialList;
export const WELCOME_ENABLED_LIST = OrigWelcomeEnabledList;
export const CUSTOM_LANDING_ENABLED_LIST = OrigCustomLandingEnabledList;
export const CUSTOM_PREFILLED_PAGE_PARTNER_LIST = OrigCustomPrefilledPagePartnerList;

// App container props handler
export const getAppContainerProps = origGetAppContainerProps;

// 'Shopfor' options type and handler
export type ShopForOptionsType = OrigShopForOptionsType;
export const getShopForOptions = origGetShopForOptions;

// Partner list for API enabled traffic.
export const API_ENABLED_LIST: PartnerName[] = ['prodigy'];

// Partner list for custom step header
export const PARTNERS_CUSTOM_STEP_HEADERS: PartnerName[] = ['mint'];

// URL path for Acorns virtual URL
export const ACORNS_URL = '/t/ac';

// Partner list for grey confetti color on finished page
export const PARTNERS_FINISHED_GREY_CONFETTI: PartnerName[] = ['mint', 'stash'];

// Partner list for grey jerry logo color on finished page
export const PARTNERS_FINISHED_GREY_LOGO: PartnerName[] = ['mint', 'nerdwallet', 'stash'];

// Partner list for black jerry logo color on finished page
export const PARTNERS_FINISHED_BLACK_LOGO: PartnerName[] = ['lyft'];

// Partner list for displaying partner logo on finished page
export const PARTNERS_FINISHED_DISPLAY_PARTNER_LOGO: PartnerName[] = ['lyft'];

// Note: 'cbautogroup' partner wants to show different logo per campaign.
export enum CBAUTO {
  cbautogroupunion = 'cbautogroupunion',
  cbautogroupadvantage = 'cbautogroupadvantage',
}

// Partner logo name types.
export type PartnerLogoType = PartnerName | keyof typeof CBAUTO;

// The partner welcome page types, currently we have these types
export enum PartnerWelcomePageType {
  InterstitialWithPhone = 'InterstitialWithPhone', // Used for facebook
  InterstitialWithQuote = 'InterstitialWithQuote', // Used for MA prefill quoting
  WelcomeLanding = 'WelcomeLanding', // If prefilled name
  WelcomeLandingPrefill = 'WelcomeLandingPrefill', // Prefill quoting test
  PartnerLanding = 'PartnerLanding', // Special landing page for partner
  Interstitial = 'Interstitial',
}

/**
 * Partner logo mapper
 * Note: 'cbautogroup' partner wants to show different logo per campaign
 */
const PARTNER_LOGO: { [key in PartnerLogoType]?: string } = {
  beenverified: BennverifiedLogo,
  bumper: BumperLogo,
  cbautogroupadvantage: CBAutoGroupAdvantageLogo,
  cbautogroupunion: CBAutoGroupUnionLogo,
  copilot: CopilotLogo,
  experian: ExperianLogo,
  fiona: FionaLogo,
  lyft: LyftLogo,
  mint: MintLogo,
  nonda: NondaLogo,
  stash: StashLogo,
  wonolo: WonoloLogo,
};

interface PartnerLogoProps {
  width: string;
  partnerLogo?: string;
}

/**
 * Get partner logo props per partner.
 * @param partnerName
 */
export const getPartnerLogoProps = (partnerName: PartnerLogoType): PartnerLogoProps => {
  const partnerLogo = PARTNER_LOGO[partnerName];

  let width = '3.125rem';
  if (includes(['bumper', 'experian', 'mint'], partnerName)) width = 'inherit';
  if (includes(['beenverified', 'cbautogroup'], partnerName)) width = '7rem';
  if (includes(values(CBAUTO), partnerName)) width = '11rem';
  if (includes(['lyft'], partnerName)) width = '2rem';

  return {
    partnerLogo,
    width,
  };
};

interface PartnerFinishColorProps {
  confettiGrey?: boolean;
  jerryLogoColor?: JerryLogoColor;
  partnerLogo?: string;
}

/**
 * Get Jerry logo color on finished step.
 */
const getJerryLogoColorFinishedStep = (partnerName): JerryLogoColor => {
  if (includes(PARTNERS_FINISHED_GREY_LOGO, partnerName)) return 'grey';
  if (includes(PARTNERS_FINISHED_BLACK_LOGO, partnerName)) return 'black';
  return undefined;
};

/**
 * Get confetti and jerry logo color on finished page per partner
 * @param partnerName
 */
export const getFinishedStepProps = (partnerName?: PartnerName): PartnerFinishColorProps => {
  const confettiGrey = includes(PARTNERS_FINISHED_GREY_CONFETTI, partnerName);
  const jerryLogoColor = getJerryLogoColorFinishedStep(partnerName);
  const partnerLogo = partnerName ? PARTNER_LOGO[partnerName] : undefined;

  return {
    confettiGrey,
    jerryLogoColor,
    partnerLogo,
  };
};

/**
 * Return first name for prefilling.
 */
export const getPrefillFirstName = (appData: AppData): string | undefined => {
  if (appData?.partner?.data?.fname) {
    return upperFirst(appData.partner.data.fname);
  }

  if (appData?.nameVariation?.firstName) {
    return upperFirst(appData.nameVariation.firstName);
  }

  if (appData?.name) {
    const firstName = getFirstName(appData.name);

    if (firstName) {
      return upperFirst(firstName);
    }
  }
};

/**
 * Get the partner welcome page type
 */
export const getPartnerWelcomePageType = (appData: AppData): PartnerWelcomePageType | undefined => {
  const partner = appData.partner?.partner;
  if (!partner) {
    return undefined;
  }
  // Partner url: signup/welcome?utm_source=everquote&fname=Radmehr&lname=Nejad&ph=9165095547&clickID=cd0c1e14-6c71-11ef-a52f-8a9016e7cab1&campaignID=
  if (appData.featureData?.askPhoneNamePrefilled && appData.featureData?.namePrefilledFromUrlParam) {
    return PartnerWelcomePageType.WelcomeLanding;
  }
  if (includes(CUSTOM_LANDING_ENABLED_LIST, partner)) {
    return PartnerWelcomePageType.PartnerLanding;
  }
  if (appData?.ptoken && appData?.id && !!appData.featureData?.prefillQuotingVariation) {
    return PartnerWelcomePageType.WelcomeLandingPrefill;
  }
  if (partner === 'facebook') {
    return appData.utmSourceFromSignupQuery === 'facebook' ? PartnerWelcomePageType.InterstitialWithPhone : undefined;
  }
  return PartnerWelcomePageType.Interstitial;
};
